import React from "react";
import styled from "styled-components";
import { NavLink, useLocation } from "react-router-dom";
import { useQuery, gql } from '@apollo/client';
import { Label, Icon } from "semantic-ui-react";
import { DownOutlined } from "@ant-design/icons";

const NUEVOS_CHATS = gql`
  {
    nuevoMensaje @client
  }
`;

const Container = styled.div`
  height: 60px;
  position: fixed;
  top: 15px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  width: fit-content;
  max-width: 95%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  border-radius: 50px;
  ${props => props.theme.glass};
  border: 1px solid ${props => props.theme.colors.glassBorder};
  transition: ${props => props.theme.transitions.soft};

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    transform: translateX(-50%) translateY(-2px);
  }

  @media (max-width: 1100px) {
    overflow-x: auto;
    justify-content: flex-start;
    border-radius: 12px;
    padding: 0 20px;
  }
`;

const MyMenu = styled.ul`
  list-style: none;
  display: flex;
  margin: 0;
  padding: 0;
  gap: 5px;
  align-items: center;
`;

const DropdownContent = styled.div`
  visibility: hidden;
  opacity: 0;
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%) translateY(10px);
  min-width: 220px;
  background: rgba(15, 23, 42, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
  padding: 10px;
  z-index: 1001;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  gap: 4px;

  &::before {
    content: '';
    position: absolute;
    top: -10px;
    left: 0;
    right: 0;
    height: 10px;
  }
`;

const ItemMenu = styled.li`
  position: relative;
  
  & > a, & > div.trigger {
    font-size: 13px;
    font-weight: 500;
    color: ${props => props.theme.colors.textDim};
    text-decoration: none;
    padding: 8px 14px;
    border-radius: 20px;
    transition: ${props => props.theme.transitions.soft};
    font-family: ${props => props.theme.fonts.title};
    letter-spacing: 0.5px;
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;

    &.active {
      color: ${props => props.theme.colors.text};
      background: rgba(255, 255, 255, 0.1);
    }

    &:hover {
      color: ${props => props.theme.colors.text};
      background: rgba(255, 255, 255, 0.05);
    }
  }

  &:hover ${DropdownContent} {
    visibility: visible;
    opacity: 1;
    transform: translateX(-50%) translateY(5px);
  }
`;

const SubItem = styled(NavLink)`
  padding: 10px 16px;
  border-radius: 10px;
  color: #94a3b8 !important;
  font-size: 12px;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 10px;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: white !important;
    padding-left: 20px;
  }

  &.active {
    background: rgba(56, 189, 248, 0.1);
    color: #38bdf8 !important;
  }
`;

const MenuLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  padding: 5px 16px;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const TheMenu = () => {
  const { data } = useQuery(NUEVOS_CHATS);
  const location = useLocation();



  if (location.pathname === "/") return null;

  const isAdmin = localStorage.getItem('username') === 'gliberte' || localStorage.getItem('division') === 'SUPERVISOR';

  return (
    <Container id="main-nav">
      <MyMenu>
        <ItemMenu key="home-left">
          <NavLink end to="/">
            <Icon name="home" size="large" style={{ margin: 0, color: '#38bdf8' }} />
          </NavLink>
        </ItemMenu>

        {/* VENTAS DROPDOWN */}
        <ItemMenu key="ventas-group">
          <div className="trigger">
            VENTAS <DownOutlined style={{ fontSize: 10, opacity: 0.5 }} />
          </div>
          <DropdownContent>
            <MenuLabel>Análisis de Mercado</MenuLabel>
            <SubItem to="/vision">Visión Global</SubItem>
            <SubItem to="/coberturas">Coberturas</SubItem>
            <SubItem to="/ventas">Ventas Territoriales</SubItem>
            <div style={{ height: 1, background: 'rgba(255,255,255,0.05)', margin: '4px 0' }} />
            <SubItem to="/analisis-avanzado">Análisis Libre Avanzado</SubItem>
          </DropdownContent>
        </ItemMenu>

        <ItemMenu key="prospeccion">
          <NavLink to="/prospeccion-2026">PROSPECCIÓN 2026</NavLink>
        </ItemMenu>

        <ItemMenu key="historicoporregion">
          <NavLink to="/historicoAnalisisRegion">HISTÓRICOS</NavLink>
        </ItemMenu>

        {/* ADMINISTRACION DROPDOWN */}
        {isAdmin && (
          <ItemMenu key="admin-group">
            <div className="trigger">
              ADMINISTRACIÓN <DownOutlined style={{ fontSize: 10, opacity: 0.5 }} />
            </div>
            <DropdownContent>
              <MenuLabel>Herramientas Admin</MenuLabel>
              <SubItem to="/historicosAdmin">Admin Históricos</SubItem>
              <SubItem to="/despacho">Ruteo de Despacho</SubItem>
              <SubItem to="/despacho/ejecucion">Ejecución del Día</SubItem>
              <SubItem to="/vendedores">Distribución de Vendedores</SubItem>
              <SubItem to="/admin/usuarios">Usuarios</SubItem>
              <SubItem to="/admin/vehiculos">Flota de Despacho</SubItem>
              <SubItem to="/monitor-tareas">Monitor de Tareas</SubItem>
              <SubItem to="/bma">BMA</SubItem>
            </DropdownContent>
          </ItemMenu>
        )}



        <ItemMenu key="ayuda">
          <NavLink to="/ayuda">AYUDA</NavLink>
        </ItemMenu>

        <ItemMenu key="login">
          <NavLink to="/login">
            {data && data.nuevoMensaje ? (
              <Label circular color="red" size="mini" empty />
            ) : null}
            LOGIN
          </NavLink>
        </ItemMenu>


      </MyMenu>
    </Container>
  );
};

export default TheMenu;
